import type { GeocodingFeature } from "../models/mapboxModels"
import type { UserProfile, EventOccurrence } from "../models/models"
import { DateTime } from "luxon"

type ZodiacSign = {
    name: string
    emoji: string
}

const zodiacSigns: { startDate: { month: number; day: number }; sign: ZodiacSign }[] = [
    { startDate: { month: 1, day: 20 }, sign: { name: "Aquarius", emoji: "♒" } },
    { startDate: { month: 2, day: 19 }, sign: { name: "Pisces", emoji: "♓" } },
    { startDate: { month: 3, day: 21 }, sign: { name: "Aries", emoji: "♈" } },
    { startDate: { month: 4, day: 20 }, sign: { name: "Taurus", emoji: "♉" } },
    { startDate: { month: 5, day: 21 }, sign: { name: "Gemini", emoji: "♊" } },
    { startDate: { month: 6, day: 21 }, sign: { name: "Cancer", emoji: "♋" } },
    { startDate: { month: 7, day: 23 }, sign: { name: "Leo", emoji: "♌" } },
    { startDate: { month: 8, day: 23 }, sign: { name: "Virgo", emoji: "♍" } },
    { startDate: { month: 9, day: 23 }, sign: { name: "Libra", emoji: "♎" } },
    { startDate: { month: 10, day: 23 }, sign: { name: "Scorpio", emoji: "♏" } },
    { startDate: { month: 11, day: 22 }, sign: { name: "Sagittarius", emoji: "♐" } },
    { startDate: { month: 12, day: 22 }, sign: { name: "Capricorn", emoji: "♑" } },
]

/**
 * Calculate zodiac sign for a given date
 * @param dateStr ISO date string (e.g., "1990-07-15")
 * @returns Zodiac sign with name and emoji
 */
export function getZodiacSign(dateStr: string): ZodiacSign | null {
    const date = DateTime.fromISO(dateStr)
    if (!date.isValid) return null

    const month = date.month
    const day = date.day

    // Find the zodiac sign for this date
    for (let i = 0; i < zodiacSigns.length; i++) {
        const current = zodiacSigns[i]!
        const next = zodiacSigns[(i + 1) % zodiacSigns.length]!

        // Check if date falls within this sign's range
        if (
            (month === current.startDate.month && day >= current.startDate.day) ||
            (month === next.startDate.month && day < next.startDate.day) ||
            (current.startDate.month < next.startDate.month && month > current.startDate.month && month < next.startDate.month)
        ) {
            return current.sign
        }
    }

    // If we get here, it's Capricorn (Dec 22 - Jan 19)
    return zodiacSigns[11]!.sign
}

export function capitalize(s: string | undefined): string {
    if (!s || s.length === 0) {
        return ""
    }
    return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * Converts a number (0-9) to its word representation with first letter capitalized
 * For numbers 10 and above, returns the number as a string
 */
export function numberToWord(num: number): string {
    const words = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"]

    // If within range 0-9, return the word
    if (num >= 0 && num < 10) {
        // Capitalize first letter
        const word = words[num]!
        return word.charAt(0).toUpperCase() + word.slice(1)
    }

    // Otherwise return the number as string
    return num.toString()
}

/**
 * Converts various phone number formats to E.164 format
 * Handles: "+1 (123) 456-7890", "+11234567890", "(123)456-7890", "1234567890", etc.
 * 
 * Can be called two ways:
 * 1. formatPhoneToE164(phoneNumber) - auto-detects or defaults to US
 * 2. formatPhoneToE164(countryCode, phoneNumber) - uses specified country code
 * 
 * @returns Phone number in E.164 format (e.g., "+11234567890")
 */
export function formatPhoneToE164(phoneInputOrCountryCode: string, phoneNumber?: string): string {
    // Handle both signatures
    let phoneInput: string
    let defaultCountryCode: string
    
    if (phoneNumber !== undefined) {
        // Old signature: formatPhoneToE164(countryCode, phoneNumber)
        defaultCountryCode = phoneInputOrCountryCode || "+1"
        phoneInput = phoneNumber
    } else {
        // New signature: formatPhoneToE164(phoneNumber)
        phoneInput = phoneInputOrCountryCode
        defaultCountryCode = "+1"
    }
    
    if (!phoneInput) return ""
    
    // Remove all non-digit characters except the leading +
    let cleaned = phoneInput.trim()
    
    // Check if it starts with + (international format)
    const hasCountryCode = cleaned.startsWith("+")
    
    // Extract just the digits
    const digits = cleaned.replace(/\D/g, "")
    
    if (!digits) return ""
    
    // If it already had a +, it's likely already in international format
    if (hasCountryCode) {
        return "+" + digits
    }
    
    // For US numbers: if it starts with 1 and has 11 digits, it already includes country code
    if (defaultCountryCode === "+1" && digits.length === 11 && digits.startsWith("1")) {
        return "+" + digits
    }
    
    // For US numbers: if it's 10 digits, add the US country code
    if (defaultCountryCode === "+1" && digits.length === 10) {
        return "+1" + digits
    }
    
    // For other cases, prepend the default country code if no country code was detected
    // Remove the + from defaultCountryCode for concatenation
    const countryDigits = defaultCountryCode.replace("+", "")
    
    // Check if the digits already start with the country code
    if (digits.startsWith(countryDigits)) {
        return "+" + digits
    }
    
    // Otherwise, add the country code
    return defaultCountryCode + digits
}

export function humanReadablePhone(e164: string | undefined): string {
    if (!e164) return ""

    // Special formatting for US numbers with +1
    if (e164.startsWith("+1") && e164.length === 12) {
        const areaCode = e164.slice(2, 5)
        const firstPart = e164.slice(5, 8)
        const secondPart = e164.slice(8)
        return `(${areaCode}) ${firstPart}-${secondPart}`
    }

    // Also format 10-digit numbers without country code as US numbers
    const digitsOnly = e164.replace(/\D/g, "")
    if (digitsOnly.length === 10) {
        const areaCode = digitsOnly.slice(0, 3)
        const firstPart = digitsOnly.slice(3, 6)
        const secondPart = digitsOnly.slice(6)
        return `(${areaCode}) ${firstPart}-${secondPart}`
    }

    // Format 11-digit numbers starting with 1 as US numbers
    if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
        const areaCode = digitsOnly.slice(1, 4)
        const firstPart = digitsOnly.slice(4, 7)
        const secondPart = digitsOnly.slice(7)
        return `(${areaCode}) ${firstPart}-${secondPart}`
    }

    // Generic formatting: Insert a space after the country code (assumed to be 1-3 digits)
    const regex = /^\+(\d{1,3})(\d+)/
    const match = e164.match(regex)
    if (match) {
        return `+${match[1]} ${match[2]}`
    }
    return e164
}

/**
 * Normalizes a phone number into multiple possible formats for matching purposes.
 * Returns an array of normalized phone number formats to try when matching against stored data.
 */
export function normalizePhoneNumber(phone: string): string[] {
    const trimmed = phone.trim()

    // If already in E164 format (starts with +), use as-is
    if (trimmed.startsWith("+")) {
        return [trimmed]
    }

    // Extract digits only
    const digitsOnly = trimmed.replace(/\D/g, "")

    // Try common formats:
    const formats = [digitsOnly]

    // If it looks like a US number (10 digits), try with +1
    if (digitsOnly.length === 10) {
        formats.push(`+1${digitsOnly}`)
    }

    // If it looks like it might be missing a country code, try with +1
    if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
        formats.push(`+${digitsOnly}`)
    }

    return formats
}

export function userDisplayName(user: UserProfile | undefined | null): string {
    if (!user) {
        return "Unknown User"
    }

    // Check if user account is deleted (soft delete)
    if ("is_active" in user && user.is_active === false) {
        return "Deleted User"
    }

    const { first_name, last_name, display_name } = user
    if (display_name) {
        return display_name
    } else if (first_name && last_name) {
        return `${first_name} ${last_name}`
    } else if (first_name) {
        return first_name
    } else if (last_name) {
        return last_name
    }
    return "Unknown User"
}

/**
 * Formats a hang title based on creator profile, location, and time information
 * @param creatorProfile - The profile of the hang creator
 * @param locationName - Primary location name
 * @param locationAddress - Backup location address
 * @param startDateTime - Event start time as Luxon DateTime
 * @param endDateTime - Optional event end time as Luxon DateTime
 * @param isCurrentUser - Whether this hang belongs to the current user
 * @returns Formatted hang title
 */
export function formatHangTitle(
    creatorProfile: UserProfile | null | undefined,
    locationName: string,
    locationAddress: string,
    startDateTime: DateTime,
    endDateTime: DateTime | null,
    isCurrentUser: boolean = false
): string {
    if (!creatorProfile) return "Hang happening"

    // Format creator name with first name and last initial, or use "You" for current user
    const creatorName = isCurrentUser
        ? "You"
        : `${creatorProfile.first_name || ""} ${creatorProfile.last_name ? creatorProfile.last_name.charAt(0) + "." : ""}`

    // Use provided location name or address as fallback
    const displayLocation = locationName || locationAddress

    // Format times
    const startTimeLabel = lowercaseMeridiem(startDateTime.toFormat("h:mma"))
    const endTimeLabel = endDateTime ? lowercaseMeridiem(endDateTime.toFormat("h:mma")) : ""

    // Check if hang has already started
    const now = DateTime.now()
    const hasStarted = startDateTime < now

    // Create title with appropriate wording based on timing
    if (hasStarted) {
        if (endDateTime && endDateTime > now) {
            return `${creatorName} ${isCurrentUser ? "are" : "is"} hanging at ${displayLocation} until ${endTimeLabel}`
        } else {
            return `${creatorName} ${isCurrentUser ? "are" : "is"} hanging at ${displayLocation} now`
        }
    } else {
        // Future hang (original logic)
        if (endDateTime) {
            return `${creatorName} ${
                isCurrentUser ? "will be" : "will be"
            } hanging at ${displayLocation} from ${startTimeLabel} to ${endTimeLabel}`
        } else {
            return `${creatorName} ${isCurrentUser ? "will be" : "will be"} hanging at ${displayLocation} starting at ${startTimeLabel}`
        }
    }
}

const WELCOME_TITLES = [
    "Hello there!",
    "Hi, [NAME]",
    "Together again!",
    "Nice to see you.",
    "Hey friend!",
    "Welcome back!",
    "Howdy, [NAME]",
    "Hey there, [NAME]!",
    "Good to see you.",
    "All together now!",
    "Greetings [NAME]",
    "Hello, NAME",
    "What's up, [NAME]",
    "Cheers, [NAME]!",
    "👋, [NAME]",
    "Greetings, friend!",
    "Your club awaits!",
    "[NAME] returns!",
]

/**
 * Returns a random welcome title, replacing [NAME] or NAME with the provided name if present.
 * @param name Optional name to personalize the title
 */
export function randomWelcomeTitle(name?: string): string {
    const idx = Math.floor(Math.random() * WELCOME_TITLES.length)
    let title = WELCOME_TITLES[idx] ?? "Welcome!"
    if (name) {
        title = title.replace(/\[NAME\]|NAME/g, name)
    } else {
        // Remove [NAME] or NAME if no name is provided
        title = title.replace(/,? ?\[NAME\]|,? ?NAME/g, "")
    }
    // Clean up any double spaces or trailing commas
    return title.replace(/  +/g, " ").replace(/,\s*$/, "").trim()
}

export function locationAreaName(location: GeocodingFeature | null | undefined): string {
    if (!location) return "Unknown Location"
    // If we have a neighborhood, use <neighborhood>, <city>. Otherwise, use <city>, <state>.
    const neighborhood = location.properties.neighborhood
    const city = location.properties.city
    const state = location.properties.state
    if (neighborhood) {
        return `${neighborhood}, ${city}`
    }
    if (city || state) {
        return `${city || ""}, ${state || ""}`
    } else {
        return "Unknown Location"
    }
}

/**
 * Lowercases AM/PM when it's:
 *  – surrounded by non‐word chars (i.e. a true word boundary), or
 *  – immediately preceded by a digit.
 */
export function lowercaseMeridiem(input: string): string {
    return input.replace(/AM|PM/g, (match, offset, str) => {
        const before = str[offset - 1]
        const after = str[offset + match.length]

        const isWordBoundary = (offset === 0 || /\W/.test(before)) && (offset + match.length === str.length || /\W/.test(after))

        const precededByDigit = before != null && /\d/.test(before)

        return isWordBoundary || precededByDigit ? match.toLowerCase() : match
    })
}

/**
 * Formats event date display for EventImageBuilder
 * Returns 3 lines of text based on the event's timing and year
 */
export interface EventDateDisplay {
    line1: string
    line2: string
    line3: string
}

export function formatEventDateDisplay(eventOccurrence: EventOccurrence): EventDateDisplay {
    const now = DateTime.now()
    const currentYear = now.year

    // Get timezone from the event or default to local
    const timezone = eventOccurrence.rootEvent.timezone || "local"

    // Parse occurrence times in the event's timezone
    const startTime = DateTime.fromISO(eventOccurrence.occurrence_start, { zone: timezone })
    const endTime = eventOccurrence.occurrence_end ? DateTime.fromISO(eventOccurrence.occurrence_end, { zone: timezone }) : null

    // Check if it's a multi-day event (different days)
    const isMultiDay = endTime && !startTime.hasSame(endTime, "day")

    // Check if the start year is different from current year
    const isDifferentYear = startTime.year !== currentYear

    // Helper function to format time (e.g., "8pm" or "8:15pm")
    const formatTime = (dateTime: DateTime): string => {
        const minute = dateTime.minute
        if (minute === 0) {
            return lowercaseMeridiem(dateTime.toFormat("ha"))
        } else {
            return lowercaseMeridiem(dateTime.toFormat("h:mma"))
        }
    }

    if (isMultiDay) {
        // Multi-day cases
        if (isDifferentYear || (endTime && endTime.year !== currentYear)) {
            // Multi-day (different year) - case 6
            const startShort = startTime.toFormat("EEE, M/d/yy")
            const endShort = endTime!.toFormat("EEE, M/d/yy")
            return {
                line1: `${startShort} –`,
                line2: endShort,
                line3: "",
            }
        } else {
            // Multi-day (same year) - case 5
            const startShort = startTime.toFormat("EEE, MMM d")
            const endShort = endTime!.toFormat("EEE, MMM d")
            return {
                line1: `${startShort} –`,
                line2: endShort,
                line3: "",
            }
        }
    } else {
        // Single day cases
        const dayName = startTime.toFormat("EEEE")
        const hasEndTime = endTime !== null && endTime.isValid

        if (isDifferentYear) {
            // Single day, different year - cases 3 & 4
            const dateWithYear = startTime.toFormat("MMM d, yyyy")
            if (hasEndTime) {
                // Case 4: with end time
                const timeRange = `${formatTime(startTime)} – ${formatTime(endTime!)}`
                return {
                    line1: dayName,
                    line2: dateWithYear,
                    line3: timeRange,
                }
            } else {
                // Case 3: start time only
                return {
                    line1: dayName,
                    line2: dateWithYear,
                    line3: formatTime(startTime),
                }
            }
        } else {
            // Single day, same year - cases 1 & 2
            const dateNoYear = startTime.toFormat("MMMM d")
            if (hasEndTime) {
                // Case 2: with end time
                const timeRange = `${formatTime(startTime)} – ${formatTime(endTime!)}`
                return {
                    line1: dayName,
                    line2: dateNoYear,
                    line3: timeRange,
                }
            } else {
                // Case 1: start time only
                return {
                    line1: dayName,
                    line2: dateNoYear,
                    line3: formatTime(startTime),
                }
            }
        }
    }
}
